import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import your icons
import tshirtIcon from "../assets/icon/1.png";
import uploadIcon from "../assets/icon/2.png";
import printIcon from "../assets/icon/3.png";

// Register ScrollTrigger globally once when the module loads
// This is good practice to ensure it's ready before any component uses it
gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    id: 1,
    icon: tshirtIcon,
    title: "Choose Your T-Shirt",
    description: "Select from premium-quality styles, sizes, and colors.",
  },
  {
    id: 2,
    icon: uploadIcon,
    title: "Upload or Design",
    description: "Use our easy online designer or upload your own artwork.",
  },
  {
    id: 3,
    icon: printIcon,
    title: "Print & Deliver",
    description: "We’ll print with care and ship straight to your door.",
  },
];

function Process() {
  const sectionRef = useRef(null);
  // NOTE: ScrollTrigger is registered globally outside the component

  useEffect(() => {
    const section = sectionRef.current;
    
    // Check if the section reference is available
    if (!section) return; 

    // Define the GSAP context to isolate animations (best practice in React)
    let ctx = gsap.context(() => {
        const items = section.querySelectorAll(".process-step");

        // Create the ScrollTrigger animation
        gsap.from(items, {
          scrollTrigger: {
            trigger: section,
            // Ensure the container is available and fully rendered
            start: "top 85%", // trigger when section comes into view
            toggleActions: "play none none reset",
            // Optional: for better debugging
            // markers: true, 
          },
          y: 100, // slide up from below
          opacity: 0,
          stagger: 0.3, // delay between each item
          duration: 1,
          ease: "power3.out",
        });

    }, section); // <- Scopes the selector engine to the section element

    // Cleanup function: important for React components
    return () => ctx.revert(); // <-- Reverts all GSAP animations and ScrollTriggers created in this context
  }, []); // Empty dependency array ensures it runs only once after mount

  return (
    <section
      ref={sectionRef}
      className="container mx-auto px-6 py-12  border-b-solid"
      id="process-section"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className="process-step flex flex-col items-center text-center relative group"
          >
            {/* Icon */}
            <img
              src={step.icon}
              alt={step.title}
              className="w-16 h-16 mb-4 transition-transform duration-300 group-hover:scale-110"
            />

            {/* Title */}
            <h3 className="text-lg md:text-xl font-bold mb-2 font-outfit">{step.title}</h3>

            {/* Description */}
            <p className="text-gray-600 text-sm md:text-base max-w-[250px] font-custom">
              {step.description}
            </p>

            {/* Divider (only for desktop and not last item) */}
            {index !== steps.length - 1 && (
              <div className="hidden md:block absolute right-0 top-1/2 h-10 border-r border-gray-300 transform -translate-y-1/2"></div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export default Process;